// MobileNotificationsPage.jsx — conteneur mobile de l'écran Notifications.
// Récupère les notifications depuis l'API, les adapte au view-model et câble
// les callbacks de persistance (lu / non-lu / tout lu / écarter) + le toggle
// « Sons ». Monté par ResponsiveNotifications sous 768px (route /notifications).
//
// Principe anti-clobber : l'état optimiste (lu, animation de retrait, annulation)
// est porté par <NotificationsMobile>. Le conteneur ne modifie PAS `raw` lors
// d'une action — `vm` (mémoïsé) garde donc sa référence et ne réécrase jamais
// l'état du composant en plein geste. `raw` ne change qu'à un vrai rechargement
// (montage, « Rafraîchir », changement société/magasin) → resync vers la vérité
// serveur.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PLANNING_NOTIFICATION_EVENT } from "../../utils/planningNotificationStream";
import { useNavigate, useOutletContext } from "react-router-dom";
import { message } from "antd";
import apiClient from "../../api/apiClient";
import { createScopedRequestGate, isScopedRequestCanceled } from "../../utils/scopedRequestGate";
import { useAuth } from "../../context/AuthContext";
import { useSelection } from "../../SelectionContext";
import { useTheme } from "../../context/ThemeContext";
import {
  getBusinessAudioSettings,
  enableBusinessAudioAlerts,
  saveBusinessAudioSettings,
  subscribeToBusinessAudioSettings,
} from "../../utils/restaurantAudio";
import NotificationsMobile from "./NotificationsMobile";
import adaptNotification from "./adaptNotification";
import "./notifications-mobile.css";

// Canal « notifications » actif = son maître ON ET canal notifications ON.
const audioOn = (s) => Boolean(s.enabled && s.notifications);

// Délai (ms) avant persistance réelle d'un « écarter » — laisse la fenêtre
// d'annulation du toast (4200ms) opérer avant d'appeler le backend.
const DISMISS_DELAY = 4300;

export default function MobileNotificationsPage() {
  const navigate = useNavigate();
  const outletContext = useOutletContext();
  const { user } = useAuth();
  const { selectedCompany, selectedWarehouse } = useSelection();
  const { isDarkMode } = useTheme();

  const [raw, setRaw] = useState([]);
  const [, setLoading] = useState(false);
  const [sons, setSons] = useState(() => audioOn(getBusinessAudioSettings()));

  // Refs pour des callbacks stables (pas de re-render inutile, pas de resync).
  const bodyRef = useRef({});
  bodyRef.current = { user_id: user?.id, company_id: selectedCompany };
  const warehouseRef = useRef(null);
  warehouseRef.current = selectedWarehouse || undefined;
  const pending = useRef(new Map()); // id -> timeoutId des « écarter » en attente
  const scopeKey = `${user?.id}:${selectedCompany}:${selectedWarehouse}`;
  const currentScope = useRef(scopeKey); currentScope.current = scopeKey;
  const requestGate = useRef(createScopedRequestGate());

  // Synchronise le toggle « Sons » avec le store réel (cloche, autre onglet…).
  useEffect(() => subscribeToBusinessAudioSettings((s) => setSons(audioOn(s))), []);

  const fetchList = useCallback(async (showLoading = true) => {
    if (!user?.id || !selectedCompany) {
      setRaw([]);
      return;
    }
    const request = requestGate.current.start(scopeKey);
    if (showLoading) setLoading(true);
    try {
      const response = await apiClient.get("/api/notifications", {
        params: {
          company_id: selectedCompany,
          user_id: user.id,
          warehouse_id: selectedWarehouse || undefined,
          page: 1,
          limit: 50,
          sort_by: "created_at",
          sort_order: "DESC",
        },
        signal: request.signal, skipGlobalLoading: !showLoading,
      });
      if (!request.isCurrent(currentScope.current)) return;
      setRaw(response.data?.notifications || []);
    } catch (e) {
      if (isScopedRequestCanceled(e, request.signal) || !request.isCurrent(currentScope.current)) return;
      message.error("Erreur lors du chargement des notifications");
    } finally {
      const current = request.isCurrent(currentScope.current); request.finish();
      if (current) setLoading(false);
    }
  }, [scopeKey, user?.id, selectedCompany, selectedWarehouse]);

  useEffect(() => { setRaw([]); }, [scopeKey]);
  useEffect(() => {
    fetchList();
    const refresh = () => { void fetchList(false); };
    window.addEventListener(PLANNING_NOTIFICATION_EVENT, refresh);
    return () => { requestGate.current.cancel(); window.removeEventListener(PLANNING_NOTIFICATION_EVENT, refresh); };
  }, [fetchList]);

  // Référence stable tant que `raw` ne change pas (anti-clobber).
  const vm = useMemo(() => raw.map(adaptNotification), [raw]);

  const handleMarkRead = useCallback((n, read) => {
    const url = read
      ? `/api/notifications/mark-read/${n.id}`
      : `/api/notifications/mark-unread/${n.id}`;
    apiClient.post(url, bodyRef.current).catch(() => {});
  }, []);

  const handleMarkAllRead = useCallback(() => {
    apiClient
      .post("/api/notifications/mark-all-read", {
        ...bodyRef.current,
        warehouse_id: warehouseRef.current,
      })
      .catch(() => {});
  }, []);

  // Écarter : persistance différée (annulable via le toast).
  const handleDelete = useCallback((n) => {
    if (pending.current.has(n.id)) return;
    const timeoutId = setTimeout(() => {
      pending.current.delete(n.id);
      apiClient.post(`/api/notifications/dismiss/${n.id}`, bodyRef.current).catch(() => {});
    }, DISMISS_DELAY);
    pending.current.set(n.id, timeoutId);
  }, []);

  const handleUndoDelete = useCallback((n) => {
    const timeoutId = pending.current.get(n.id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      pending.current.delete(n.id);
    }
  }, []);

  // Au démontage : confirme les « écarter » encore en attente (pas d'annulation
  // possible une fois la page quittée).
  useEffect(() => {
    const map = pending.current;
    return () => {
      map.forEach((timeoutId, id) => {
        clearTimeout(timeoutId);
        apiClient.post(`/api/notifications/dismiss/${id}`, bodyRef.current).catch(() => {});
      });
      map.clear();
    };
  }, []);

  const handleOpenEntity = useCallback(
    (n) => {
      if (n.cta?.to) navigate(n.cta.to);
    },
    [navigate]
  );

  // Accès aux réglages de notifications (types + canaux), scopé au magasin courant.
  const handleOpenSettings = useCallback(() => {
    const warehouseId = warehouseRef.current;
    navigate(
      warehouseId
        ? `/notification-settings?warehouseId=${warehouseId}`
        : "/notification-settings"
    );
  }, [navigate]);

  const handleToggleSons = useCallback(async () => {
    if (audioOn(getBusinessAudioSettings())) {
      saveBusinessAudioSettings({ notifications: false });
    } else {
      await enableBusinessAudioAlerts({ notifications: true });
    }
  }, []);

  return (
    <NotificationsMobile
      notifications={vm}
      onMenu={outletContext?.openMobileMenu}
      dark={isDarkMode}
      sons={sons}
      onToggleSons={handleToggleSons}
      onBack={() => navigate("/accueil")}
      onOpenEntity={handleOpenEntity}
      onMarkRead={handleMarkRead}
      onMarkAllRead={handleMarkAllRead}
      onDelete={handleDelete}
      onUndoDelete={handleUndoDelete}
      onRefresh={fetchList}
      onOpenSettings={handleOpenSettings}
    />
  );
}
